// atomWithSessionDayjsStorage.ts

import {atom, PrimitiveAtom, SetStateAction, WritableAtom} from 'jotai';
import {atomWithStorage, createJSONStorage} from 'jotai/utils';
import dayjs, {Dayjs} from 'dayjs';

function sessionDayjsJsonStorage<T>() {
   return createJSONStorage<T>(() => sessionStorage, {
      reviver: (_key, value) => {
         if (typeof value === 'string') {
            const d = dayjs(value);
            if (d.isValid()) {
               return d;
            }
         }

         return value;
      },

      replacer: (_key, value) => {
         if (dayjs.isDayjs(value)) {
            return value.toISOString();
         }

         return value;
      },
   });
}

/**
 * A Jotai atom that stores a Dayjs value in sessionStorage as an ISO string.
 *
 * Example:
 *
 * export const reportDateAtom = atomWithSessionDayjsStorage(
 *   'reportDate',
 *   dayjs(),
 * );
 */
export function atomWithSessionDayjsStorage(key: string, initialValue: Dayjs): PrimitiveAtom<Dayjs> {
   return atomWithStorage<Dayjs>(key, initialValue, sessionDayjsJsonStorage<Dayjs>());
}

/**
 * Like atomWithSessionDayjsStorage, but for a range's "To" date that should
 * mean "through today" until the user picks an earlier day.
 *
 * A plain session-stored default (`dayjs().endOf('day')`) is frozen the first
 * time it is saved, and sessionStorage survives refreshes, so a tab first
 * opened on 10/16 kept "To = Oct 16" (and greyed out every later date) for
 * as long as the tab lived. Here, picking today's date (or never touching
 * the picker) stores `null`, meaning "follow today", and the value is
 * resolved against the current day every time it is read. Picking any other
 * day stores that exact day as before.
 */
export function atomWithSessionDayjsFollowingToday(
   key: string,
   boundary: 'startOf' | 'endOf' = 'endOf',
): WritableAtom<Dayjs, [SetStateAction<Dayjs>], void> {
   const today = () => (boundary === 'endOf' ? dayjs().endOf('day') : dayjs().startOf('day'));
   const stored = atomWithStorage<Dayjs | null>(key, null, sessionDayjsJsonStorage<Dayjs | null>(), {getOnInit: true});

   return atom(
      (get) => get(stored) ?? today(),
      (get, set, update) => {
         const next = typeof update === 'function' ? update(get(stored) ?? today()) : update;
         set(stored, next.isSame(dayjs(), 'day') ? null : next);
      },
   );
}
